import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Lock, 
  LogOut, 
  Inbox, 
  CheckCircle, 
  MessageSquare, 
  Archive, 
  Trash2, 
  Eye, 
  Mail, 
  Phone, 
  User, 
  Calendar,
  BarChart3,
  RefreshCw,
  ChevronLeft,
  Building2,
  Gem,
  Palette,
  TrendingUp,
  Truck,
  Banknote
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service icon mapping
const serviceIcons = {
  "real-estate": Building2,
  "jewelry": Gem,
  "art": Palette,
  "sales-distribution": TrendingUp,
  "logistics": Truck,
  "liquidation": Banknote,
};

// Status colors and labels
const statusConfig = {
  new: { label: "New", color: "bg-blue-500", textColor: "text-blue-400" },
  read: { label: "Read", color: "bg-yellow-500", textColor: "text-yellow-400" },
  responded: { label: "Responded", color: "bg-green-500", textColor: "text-green-400" },
  archived: { label: "Archived", color: "bg-gray-500", textColor: "text-gray-400" },
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem("adminAuth", "true");
        onLogin();
      }
    } catch (err) {
      setError("Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block mb-6">
            <div className="border border-white/20 px-6 py-4">
              <span className="font-serif text-2xl text-white tracking-wider">B | F</span>
            </div>
          </div>
          <h1 className="text-3xl text-white font-serif mb-2">Admin Dashboard</h1>
          <p className="text-[#A3A3A3]">Enter your password to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 p-8">
          <div className="mb-6">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="admin-password-input"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-4 pl-10 text-white placeholder:text-white/30 outline-none transition-colors"
              />
            </div>
          </div>

          {error && (
            <p className="text-red-400 text-sm mb-4" data-testid="login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            data-testid="admin-login-btn"
            className="w-full bg-white text-black py-4 font-bold uppercase tracking-widest text-xs hover:bg-[#D4AF37] transition-colors disabled:opacity-50"
          >
            {isLoading ? "Verifying..." : "Access Dashboard"}
          </button>
        </form>

        <div className="text-center mt-6">
          <a href="/" className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors text-sm flex items-center justify-center gap-2">
            <ChevronLeft size={16} />
            Back to Website
          </a>
        </div>
      </div>
    </div>
  );
};

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[#0A0A0A] border border-white/5 p-6">
    <div className="flex items-center gap-4">
      <div className={`p-3 ${color} bg-opacity-10 rounded`}>
        <Icon size={24} className={color.replace("bg-", "text-")} />
      </div>
      <div>
        <p className="text-[#A3A3A3] text-sm uppercase tracking-wider">{label}</p>
        <p className="text-white text-2xl font-semibold">{value}</p>
      </div>
    </div>
  </div>
);

// Inquiry Card Component
const InquiryCard = ({ inquiry, onStatusChange, onDelete, onView }) => {
  const ServiceIcon = serviceIcons[inquiry.service_interest] || Inbox;
  const status = statusConfig[inquiry.status] || statusConfig.new;
  const createdAt = new Date(inquiry.created_at).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div 
      className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#D4AF37]/30 transition-all"
      data-testid={`inquiry-card-${inquiry.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${status.color}`}></div>
          <span className={`text-xs uppercase tracking-wider ${status.textColor}`}>
            {status.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onView(inquiry)}
            className="p-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
            title="View Details"
            data-testid={`view-inquiry-${inquiry.id}`}
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => onDelete(inquiry.id)}
            className="p-2 text-[#A3A3A3] hover:text-red-400 transition-colors"
            title="Delete"
            data-testid={`delete-inquiry-${inquiry.id}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <User size={14} className="text-[#D4AF37]" />
          <span className="text-white font-medium">{inquiry.name}</span>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Mail size={14} className="text-[#A3A3A3]" />
          <span className="text-[#A3A3A3] text-sm">{inquiry.email}</span>
        </div>
        {inquiry.phone && (
          <div className="flex items-center gap-2 mb-2">
            <Phone size={14} className="text-[#A3A3A3]" />
            <span className="text-[#A3A3A3] text-sm">{inquiry.phone}</span>
          </div>
        )}
      </div>

      {inquiry.service_interest && (
        <div className="flex items-center gap-2 mb-4 text-[#A3A3A3]">
          <ServiceIcon size={14} className="text-[#D4AF37]" />
          <span className="text-sm capitalize">{inquiry.service_interest.replace("-", " ")}</span>
        </div>
      )}

      <p className="text-[#A3A3A3] text-sm line-clamp-2 mb-4">{inquiry.message}</p>

      <div className="flex items-center justify-between pt-4 border-t border-white/5">
        <div className="flex items-center gap-2 text-[#A3A3A3]">
          <Calendar size={12} />
          <span className="text-xs">{createdAt}</span>
        </div>
        <select
          value={inquiry.status}
          onChange={(e) => onStatusChange(inquiry.id, e.target.value)}
          className="bg-transparent border border-white/10 text-white text-xs px-3 py-1 rounded cursor-pointer focus:border-[#D4AF37] outline-none"
          data-testid={`status-select-${inquiry.id}`}
        >
          <option value="new" className="bg-[#0A0A0A]">New</option>
          <option value="read" className="bg-[#0A0A0A]">Read</option>
          <option value="responded" className="bg-[#0A0A0A]">Responded</option>
          <option value="archived" className="bg-[#0A0A0A]">Archived</option>
        </select>
      </div>
    </div>
  );
};

// Inquiry Detail Modal
const InquiryModal = ({ inquiry, onClose, onStatusChange }) => {
  if (!inquiry) return null;

  const ServiceIcon = serviceIcons[inquiry.service_interest] || Inbox;
  const status = statusConfig[inquiry.status] || statusConfig.new;
  const createdAt = new Date(inquiry.created_at).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div 
        className="bg-[#0A0A0A] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="inquiry-modal"
      >
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${status.color}`}></div>
              <span className={`text-sm uppercase tracking-wider ${status.textColor}`}>
                {status.label}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-[#A3A3A3] hover:text-white transition-colors"
              data-testid="close-modal-btn"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Name</p>
              <p className="text-white text-lg">{inquiry.name}</p>
            </div>
            <div>
              <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Email</p>
              <a href={`mailto:${inquiry.email}`} className="text-[#D4AF37] hover:underline">
                {inquiry.email}
              </a>
            </div>
            <div>
              <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Phone</p>
              <p className="text-white">{inquiry.phone || "Not provided"}</p>
            </div>
            <div>
              <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Service Interest</p>
              <div className="flex items-center gap-2">
                <ServiceIcon size={16} className="text-[#D4AF37]" />
                <span className="text-white capitalize">
                  {inquiry.service_interest?.replace("-", " ") || "Not specified"}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-2">Message</p>
            <div className="bg-[#050505] border border-white/5 p-4">
              <p className="text-white whitespace-pre-wrap">{inquiry.message}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-white/5">
            <div className="text-[#A3A3A3] text-sm">
              <Calendar size={14} className="inline mr-2" />
              {createdAt}
            </div>
            <select
              value={inquiry.status}
              onChange={(e) => onStatusChange(inquiry.id, e.target.value)}
              className="bg-transparent border border-white/20 text-white px-4 py-2 cursor-pointer focus:border-[#D4AF37] outline-none"
              data-testid="modal-status-select"
            >
              <option value="new" className="bg-[#0A0A0A]">New</option>
              <option value="read" className="bg-[#0A0A0A]">Read</option>
              <option value="responded" className="bg-[#0A0A0A]">Responded</option>
              <option value="archived" className="bg-[#0A0A0A]">Archived</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const AdminDashboard = ({ onLogout }) => {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inquiriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries`),
        axios.get(`${API}/admin/stats`),
      ]);
      setInquiries(inquiriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/status`, { status: newStatus });
      setInquiries((prev) =>
        prev.map((inq) => (inq.id === id ? { ...inq, status: newStatus } : inq))
      );
      if (selectedInquiry?.id === id) {
        setSelectedInquiry((prev) => ({ ...prev, status: newStatus }));
      }
      toast.success(`Status updated to ${newStatus}`);
      fetchData(); // Refresh stats
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;

    try {
      await axios.delete(`${API}/admin/inquiries/${id}`);
      setInquiries((prev) => prev.filter((inq) => inq.id !== id));
      toast.success("Inquiry deleted");
      fetchData(); // Refresh stats
    } catch (error) {
      console.error("Error deleting inquiry:", error);
      toast.error("Failed to delete inquiry");
    }
  };

  const filteredInquiries = filter === "all" 
    ? inquiries 
    : inquiries.filter((inq) => inq.status === filter);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="border border-white/20 px-4 py-2">
              <span className="font-serif text-lg text-white tracking-wider">B | F</span>
            </a>
            <span className="text-[#A3A3A3] text-sm">Admin Dashboard</span>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 text-[#A3A3A3] hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Section */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Inbox} label="Total" value={stats.total} color="bg-[#D4AF37]" />
            <StatCard icon={MessageSquare} label="New" value={stats.by_status.new} color="bg-blue-500" />
            <StatCard icon={CheckCircle} label="Responded" value={stats.by_status.responded} color="bg-green-500" />
            <StatCard icon={Archive} label="Archived" value={stats.by_status.archived} color="bg-gray-500" />
          </div>
        )}

        {/* Filter and Refresh */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="text-[#A3A3A3] text-sm">Filter:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-transparent border border-white/10 text-white px-4 py-2 cursor-pointer focus:border-[#D4AF37] outline-none"
              data-testid="filter-select"
            >
              <option value="all" className="bg-[#0A0A0A]">All Inquiries</option>
              <option value="new" className="bg-[#0A0A0A]">New</option>
              <option value="read" className="bg-[#0A0A0A]">Read</option>
              <option value="responded" className="bg-[#0A0A0A]">Responded</option>
              <option value="archived" className="bg-[#0A0A0A]">Archived</option>
            </select>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            <span className="text-sm">Refresh</span>
          </button>
        </div>

        {/* Inquiries Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-white/5 p-6 animate-pulse">
                <div className="h-4 bg-white/10 rounded mb-4 w-1/4"></div>
                <div className="h-6 bg-white/10 rounded mb-2 w-3/4"></div>
                <div className="h-4 bg-white/10 rounded mb-4 w-1/2"></div>
                <div className="h-16 bg-white/10 rounded"></div>
              </div>
            ))}
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="text-center py-16">
            <Inbox size={48} className="mx-auto text-[#A3A3A3] mb-4" />
            <p className="text-[#A3A3A3]">No inquiries found</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInquiries.map((inquiry) => (
              <InquiryCard
                key={inquiry.id}
                inquiry={inquiry}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onView={setSelectedInquiry}
              />
            ))}
          </div>
        )}
      </main>

      {/* Inquiry Detail Modal */}
      {selectedInquiry && (
        <InquiryModal
          inquiry={selectedInquiry}
          onClose={() => setSelectedInquiry(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

// Main Admin App Component
function AdminApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = localStorage.getItem("adminAuth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setIsAuthenticated(false);
  };

  return (
    <>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0A0A0A',
            color: '#F5F5F5',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />
      {isAuthenticated ? (
        <AdminDashboard onLogout={handleLogout} />
      ) : (
        <AdminLogin onLogin={handleLogin} />
      )}
    </>
  );
}

export default AdminApp;
